/* ========================================
   INKON TATTOO QUIZ - CORE ENGINE
   PMI-based collaborative filtering
   Adaptive swipe selection
   ======================================== */

(function() {
  'use strict';

  // ==================== CONFIG ====================
  const CONFIG = {
    calibrationSkus: ['GZQB-10', 'GZ-149', 'SPX-214', 'GZXQB-IN7', 'GZQB-68', 'GZBH-041'],
    sleeveCalibrationCount: 5, // we ensure at least this many GZQB are shown across the quiz
    sleeveDetectionThreshold: 3, // min likes out of 5 GZQB to flag as sleeve-lover
    newSkus: [
      'GZQB-IN24','GZQB-IN25','GZQB-IN26','GZQB-IN27','GZQB-IN28','GZQB-IN29',
      'GZQB-IN30','GZQB-IN31','GZQB-IN32','GZQB-IN33','GZQB-IN34','GZQB-IN35',
      'GZQB-IN36','GZQB-IN37','GZQB-IN38','GZQB-IN39','GZQB-IN40','GZQB-IN41',
      'GZQB-IN42','GZQB-IN43','GZQB-IN44','GZQB-IN45',
      'GZ-IN80','GZ-IN81','GZ-IN82','GZ-IN83','GZ-IN84','GZ-IN85','GZ-IN86',
      'GZ-IN87','GZ-IN88','GZ-IN89',
      'GZQ-IN66','GZQ-IN67','GZQ-IN68',
      'GZS-IN50','GZS-IN51',
      'SPX-IN52','SPX-IN53','SPX-IN54','SPX-IN55','SPX-IN56','SPX-IN57',
      'GZXQB-IN44','GZXQB-IN45','GZXQB-IN46','GZXQB-IN47',
      'GZX-IN50','GZX-IN51','GZX-IN52'
    ],
    newSlotsCount: 5,
    totalResultSlots: 40,
    minSwipes: 15,
    stabilityThreshold: 0.85,
    forceStopThreshold: 0.95,
    consecutiveStableNeeded: 2,
    minSleeves: 3, // Minimum GZQB designs to show before quiz can end
    leftSwipeWeight: 0.3,
    decayFactor: 0.92,
    swipeThreshold: 80,
    testimonialImages: [
      'https://cdn.shopify.com/s/files/1/0701/7372/0685/files/hf_20260326_071114_5e29cd29-6e63-44bd-8130-6631509658da_1.jpg?v=1775615791',
      'https://cdn.shopify.com/s/files/1/0701/7372/0685/files/hf_20260330_020551_cbbd6806-60dc-475e-96cb-fb56106344f8_1.png?v=1775615833',
      'https://cdn.shopify.com/s/files/1/0701/7372/0685/files/hf_20260330_015257_2b8ddc37-8075-47ef-85a5-79212ab9082b_1.png?v=1775615872',
      'https://cdn.shopify.com/s/files/1/0701/7372/0685/files/hf_20260329_061809_02655752-6428-46b5-8f0d-e3d8bf546baa_1.png?v=1775616055',
      'https://cdn.shopify.com/s/files/1/0701/7372/0685/files/hf_20260329_042518_3f544cb7-62c5-4828-9947-b6dbe90473be_1.png?v=1775615811',
      'https://cdn.shopify.com/s/files/1/0701/7372/0685/files/hf_20260329_060058_f19476dd-3192-48ad-9f2c-20118e74ec06_1.png?v=1775615935'
    ]
  };

  // ==================== TRACKER ====================
  const TRACKER = {
    endpoint: 'https://inkon-tracker.kasra-mohammadali.workers.dev',
    sessionId: null,
    userId: null,
    continuation: 1,
    enabled: true,
    _queue: [],
    _pending: [],  // events fired before sessionId exists — flushed once session ready
    _sending: false,

    init() {
      let uid = localStorage.getItem('inkon_user_id');
      if (!uid) {
        uid = crypto.randomUUID();
        localStorage.setItem('inkon_user_id', uid);
      }
      this.userId = uid;
    },

    async createSession() {
      if (!this.enabled) return;
      try {
        const res = await fetch(`${this.endpoint}/api/session`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            user_id: this.userId,
            referrer: document.referrer || '',
          }),
        });
        const data = await res.json();
        this.sessionId = data.session_id;
        // Phase 1A.5 — store predicted_ltv for use in cart_attributes + Meta Pixel
        this.predictedScore = typeof data.predicted_score === 'number' ? data.predicted_score : 0.0126;
        this.predictedLtv = +(this.predictedScore * 35).toFixed(2);  // $35 = mean AOV
        this.scoreSource = data.score_source || 'unknown';
        // Persist immediately so it survives reloads and can be used as a fallback
        // when confirming the bundle (needed for Shopify order attribution).
        try {
          localStorage.setItem('inkon_session_id', this.sessionId);
          localStorage.setItem('inkon_predicted_ltv', String(this.predictedLtv));
        } catch (e) {}
        this.event('quiz_loaded', { url: location.href, predicted_ltv: this.predictedLtv, score_source: this.scoreSource });
        // Flush any events fired before session was ready
        if (this._pending.length) {
          for (const p of this._pending) {
            this._queue.push({ session_id: this.sessionId, event_type: p.type, event_data: p.data });
          }
          this._pending.length = 0;
          this._drain();
        }
      } catch (e) { console.warn('Tracker: session create failed', e); }
    },

    // Fire-and-forget event — queued and sent immediately. Events fired before
    // the session is ready are buffered and flushed by createSession().
    event(type, data) {
      if (!this.enabled) return;
      if (!this.sessionId) {
        this._pending.push({ type, data: data || {} });
        return;
      }
      console.log('[TRACKER] event:', type, data);
      this._queue.push({ session_id: this.sessionId, event_type: type, event_data: data || {} });
      this._drain();
    },

    // Reliable send for events fired right before page navigation.
    // Uses sendBeacon which the browser guarantees to deliver even if the page
    // is unloading. Falls back to fetch with keepalive as a last resort.
    beacon(type, data) {
      if (!this.enabled || !this.sessionId) return;
      console.log('[TRACKER] beacon:', type, data);
      const body = JSON.stringify({
        events: [{ session_id: this.sessionId, event_type: type, event_data: data || {} }]
      });
      const url = `${this.endpoint}/api/events`;
      try {
        if (navigator.sendBeacon) {
          const blob = new Blob([body], { type: 'application/json' });
          const ok = navigator.sendBeacon(url, blob);
          if (ok) return;
        }
      } catch (e) {}
      // Fallback: keepalive fetch survives page unload for ~64kb
      try {
        fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body,
          keepalive: true,
        });
      } catch (e) {}
    },

    async _drain() {
      if (this._sending || this._queue.length === 0) return;
      this._sending = true;
      while (this._queue.length > 0) {
        const batch = this._queue.splice(0, 10);
        try {
          const res = await fetch(`${this.endpoint}/api/events`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ events: batch }),
          });
          console.log('[TRACKER] sent', batch.length, 'events, status:', res.status);
        } catch (e) { console.warn('[TRACKER] event send failed', e); }
      }
      this._sending = false;
    },

    // Legacy wrappers — keep existing code working + send events
    bufferSwipe(sku, direction, index, dwellMs, phase) {
      this.event('swipe', { sku, dir: direction, dwell: dwellMs, idx: index, phase, cont: this.continuation });
    },

    async flushSwipes() {
      // No-op now — swipes are sent individually
    },

    async sendSurvey(sleeveAnswer, hasRealTattoos, reasons, age, otherText) {
      this.event('survey_complete', {
        sleeve_answer: sleeveAnswer,
        has_real_tattoos: hasRealTattoos,
        survey_reasons: reasons,
        survey_age: age,
        other_text: otherText || null,
      });
      // Also update session row for backwards compatibility
      if (!this.sessionId) return;
      try {
        await fetch(`${this.endpoint}/api/survey`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            session_id: this.sessionId,
            sleeve_answer: sleeveAnswer,
            has_real_tattoos: hasRealTattoos,
            survey_reasons: reasons,
            survey_age: age,
            other_text: otherText || null,
          }),
        });
      } catch (e) {}
    },

    async sendResults(favorites, recommendations, soldout, newShown) {
      this.event('results_shown', { favorites, recommendations, soldout, new_shown: newShown, continuation: this.continuation });
      // Also update session row
      if (!this.sessionId) return;
      try {
        await fetch(`${this.endpoint}/api/results`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            session_id: this.sessionId,
            favorites, recommendations, soldout, new_shown: newShown,
            continuation: this.continuation,
          }),
        });
      } catch (e) {}
    },

    sendBundleEvent(eventType, sku, price, quantity, snapshot) {
      // Single source of truth: events table
      this.event('bundle_' + eventType, { sku, price, quantity, bundle_snapshot: snapshot || null });
    },

    async tagCart() {
      if (!this.sessionId) return;
      try {
        await fetch('/cart/update.json', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            attributes: { quiz_session_id: this.sessionId },
          }),
        });
      } catch (e) {}
    },
  };

  // ==================== STATE ====================
  let pmiData = null;       // { skus, pop, neighbors }
  let products = null;      // { sku: { handle, title, price, image, ... } }
  let profileVector = {};   // skuIndex -> score
  let shownIndices = [];    // indices of SKUs shown during swipe
  let likedIndices = [];    // indices of SKUs liked (swiped right)
  let swipeCount = 0;
  let consecutiveStable = 0;
  let previousTop40 = [];
  let quizFinished = false;
  let surveyData = { realTattoos: null, reasons: [], otherText: '', age: 25 };
  let isDragging = false;
  let startX = 0, startY = 0, currentX = 0;
  let currentCard = null;
  let swipeStartTime = 0;
  let behindCardIndex = -1; // Track behind card to avoid duplicates
  let sleevesShown = 0; // Count GZQB designs shown
  let sleevesLiked = 0; // Count GZQB designs right-swiped during calibration
  let sleevePreference = null; // null=not determined, true=likes sleeves, false=no sleeves
  let sleeveQuestionShown = false; // Kept name for backward compat; set true once preference is auto-detected
  let isAnimating = false; // Lock to prevent rapid swipe overlap

  const SIZE_MAP = {
    'GZQB': '20 x 50 cm',
    'GZ': '11 x 18 cm',
    'GZS': '7 x 12 cm',
    'GZX': '8 x 11 cm',
    'GZQ': '10 x 10 cm',
    'GZBH': '6 x 24 cm',
    'GZXQB': '12 x 22 cm',
    'CBX': '6 x 10 cm',
    'SPX': '5.5 x 5.5 cm'
  };

  function getSkuSize(sku) {
    const prefix = sku.split('-')[0];
    return SIZE_MAP[prefix] || '';
  }
  let pendingFinish = false; // Wait for user to swipe current card before ending

  // Resize Shopify CDN image URLs for faster loading. Shopify CDN supports ?width=N & format=webp.
  function cdnResize(url, width) {
    if (!url) return url;
    if (!/cdn\.shopify\.com/.test(url)) return url;
    // Strip existing width param if any, then add our own
    const u = url.replace(/([?&])width=\d+/, '$1').replace(/[?&]$/, '');
    const sep = u.includes('?') ? '&' : '?';
    return `${u}${sep}width=${width}`;
  }

  // Preload calibration + some adaptive candidates so swiping feels instant.
  function preloadCalibrationImages() {
    if (!products) return;
    // Preload ALL calibration SKUs (first 6-8 swipes)
    CONFIG.calibrationSkus.forEach((sku, i) => {
      const p = products[sku];
      if (!p || !p.img) return;
      const url = cdnResize(p.img, 600);
      if (i === 0) {
        const link = document.createElement('link');
        link.rel = 'preload';
        link.as = 'image';
        link.href = url;
        link.fetchPriority = 'high';
        document.head.appendChild(link);
      }
      const img = new Image();
      img.src = url;
    });
    // Preload testimonial images (used during loading screen, so they're ready)
    CONFIG.testimonialImages.forEach(url => {
      const img = new Image();
      img.src = cdnResize(url, 400);
    });
  }

  // Preload a set of SKUs in background (used to warm cache for next recommendations)
  function preloadSkus(skus, width) {
    if (!products) return;
    skus.forEach(sku => {
      const p = products[sku];
      if (!p || !p.img) return;
      const img = new Image();
      img.src = cdnResize(p.img, width || 400);
    });
  }

  // ==================== INIT ====================
  async function init() {
    try {
      TRACKER.init();
      // Kick off data fetches in parallel with session creation.
      const [pmiRes, prodRes] = await Promise.all([
        fetch((window.QUIZ_ASSET_BASE || '') + 'pmi_matrix.json').then(r => r.json()),
        fetch((window.QUIZ_ASSET_BASE || '') + 'product_catalog.json?v=' + Date.now()).then(r => r.json()),
        TRACKER.createSession(),
      ]);
      pmiData = pmiRes;
      products = prodRes;
      // First image is already preloaded via <link rel=preload> in the liquid.
      // Warm the rest of calibration.
      preloadCalibrationImages();
      setupSwipeUI();
      setupSurveyUI();
      // Render first cards immediately — no start button, straight to swipe.
      loadNextCards();
      // Tutorial hint animation after first card is on screen.
      setTimeout(() => {
        const topCard = document.querySelector('.swipe-card.top');
        if (topCard) {
          topCard.classList.add('tutorial-anim');
          topCard.addEventListener('animationend', () => {
            topCard.classList.remove('tutorial-anim');
          }, { once: true });
        }
      }, 300);
    } catch(e) {
      console.error('Failed to load quiz data:', e);
    }
  }

  // ==================== PMI ALGORITHM ====================
  function getSkuIndex(sku) {
    return pmiData.skus.indexOf(sku);
  }

  function updateProfile(skuIndex, isLike) {
    const neighbors = pmiData.neighbors[skuIndex] || [];
    const weight = isLike ? 1.0 : -CONFIG.leftSwipeWeight;
    const decay = Math.pow(CONFIG.decayFactor, shownIndices.length);

    for (const [neighborIdx, pmiScore] of neighbors) {
      profileVector[neighborIdx] = (profileVector[neighborIdx] || 0) + weight * pmiScore * decay;
    }
    // Boost/penalize the SKU itself
    profileVector[skuIndex] = (profileVector[skuIndex] || 0) + (isLike ? 5 : -2);
  }

  function getTopN(n) {
    const scored = [];
    for (let i = 0; i < pmiData.skus.length; i++) {
      if (shownIndices.includes(i)) continue;
      scored.push({ idx: i, score: profileVector[i] || 0 });
    }
    scored.sort((a, b) => b.score - a.score);
    return scored.slice(0, n).map(s => s.idx);
  }

  function computeInformativeness(candidateIdx) {
    const neighbors = pmiData.neighbors[candidateIdx] || [];
    if (neighbors.length === 0) return 0;
    const top50 = getTopN(50);
    const neighborMap = new Map(neighbors);
    const scores = top50.map(idx => neighborMap.get(idx) || 0);
    if (scores.length === 0) return 0;
    const mean = scores.reduce((a, b) => a + b, 0) / scores.length;
    return scores.reduce((a, b) => a + (b - mean) ** 2, 0) / scores.length;
  }

  function computeDiversity(candidateIdx) {
    if (shownIndices.length === 0) return 1;
    const candNeighbors = new Set((pmiData.neighbors[candidateIdx] || []).map(x => x[0]));
    let maxSim = 0;
    for (const shownIdx of shownIndices.slice(-6)) { // Only compare to recent 6
      const shownNeighbors = new Set((pmiData.neighbors[shownIdx] || []).map(x => x[0]));
      const intersection = [...candNeighbors].filter(x => shownNeighbors.has(x)).length;
      const union = new Set([...candNeighbors, ...shownNeighbors]).size;
      const sim = union > 0 ? intersection / union : 0;
      maxSim = Math.max(maxSim, sim);
    }
    return 1 - maxSim;
  }

  function selectNextSku() {
    const excludeSet = new Set([...shownIndices, behindCardIndex]);

    // Calibration phase — find next calibration SKU not already shown or queued
    for (let c = 0; c < CONFIG.calibrationSkus.length; c++) {
      const sku = CONFIG.calibrationSkus[c];
      const idx = getSkuIndex(sku);
      if (idx >= 0 && !excludeSet.has(idx)) return idx;
    }

    // Ensure we show at least `sleeveCalibrationCount` GZQB sleeves across the quiz
    // so we can auto-detect sleeve preference. Distributes naturally via probability.
    const needsMoreSleeves = !sleeveQuestionShown && sleevesShown < CONFIG.sleeveCalibrationCount;
    let forceSleeve = false;
    if (needsMoreSleeves) {
      const maxSwipesGuess = 18;
      const swipesRemaining = Math.max(1, maxSwipesGuess - swipeCount);
      const sleevesNeeded = CONFIG.sleeveCalibrationCount - sleevesShown;
      // Probability scales so we always hit target by end of quiz.
      const forceProb = Math.min(1, sleevesNeeded / swipesRemaining);
      if (Math.random() < forceProb) forceSleeve = true;
    }
    if (forceSleeve || sleevesToForce > 0) {
      const sleeveCandidates = [];
      for (let i = 0; i < pmiData.skus.length; i++) {
        if (excludeSet.has(i)) continue;
        const sku = pmiData.skus[i];
        if (!products[sku] || !sku.startsWith('GZQB')) continue;
        sleeveCandidates.push({ idx: i, score: profileVector[i] || 0 });
      }
      if (sleeveCandidates.length > 0) {
        sleeveCandidates.sort((a, b) => b.score - a.score);
        if (sleevesToForce > 0) sleevesToForce--;
        return sleeveCandidates[0].idx;
      }
    }

    // Adaptive phase
    const candidates = [];
    for (let i = 0; i < pmiData.skus.length; i++) {
      if (excludeSet.has(i)) continue;
      const sku = pmiData.skus[i];
      if (!products[sku]) continue;
      candidates.push(i);
    }

    if (candidates.length === 0) return -1;

    const lambda = Math.min(0.3 + 0.4 * (swipeCount / 20), 0.7);
    let bestIdx = candidates[0];
    let bestScore = -Infinity;

    for (const c of candidates) {
      const info = computeInformativeness(c);
      const div = computeDiversity(c);
      const score = lambda * info + (1 - lambda) * div;
      if (score > bestScore) {
        bestScore = score;
        bestIdx = c;
      }
    }
    return bestIdx;
  }

  function checkStopCriterion() {
    if (isContinuation) {
      // In continuation mode: min 10 swipes from when continuation started
      const contSwipes = swipeCount - continuationSwipeStart;
      if (contSwipes < CONTINUATION_MIN_SWIPES) return 'continue';

      // Force stop at max
      if (contSwipes >= CONTINUATION_MAX_SWIPES) return 'force_stop';

      // Use stability check
      const currentTop40 = getTopN(40);
      if (previousTop40.length === 0) {
        previousTop40 = currentTop40;
        return 'continue';
      }
      const overlap = currentTop40.filter(i => previousTop40.includes(i)).length / 40;
      previousTop40 = currentTop40;

      if (overlap >= CONFIG.stabilityThreshold) {
        consecutiveStable++;
        if (consecutiveStable >= CONFIG.consecutiveStableNeeded) return 'force_stop';
      } else {
        consecutiveStable = 0;
      }
      return 'continue';
    }

    // Original quiz mode
    if (swipeCount < CONFIG.minSwipes) return 'continue';

    // Must have answered sleeve question before quiz can end
    if (!sleeveQuestionShown) return 'continue';

    // If too many dislikes (< 30% like rate), extend quiz to get more signal
    const likeRate = likedIndices.length / swipeCount;
    if (likeRate < 0.3 && swipeCount < 20) return 'continue';

    const currentTop40 = getTopN(40);
    if (previousTop40.length === 0) {
      previousTop40 = currentTop40;
      return 'continue';
    }
    const overlap = currentTop40.filter(i => previousTop40.includes(i)).length / 40;
    previousTop40 = currentTop40;

    if (overlap >= CONFIG.forceStopThreshold) {
      consecutiveStable++;
      if (consecutiveStable >= CONFIG.consecutiveStableNeeded) return 'force_stop';
    }
    if (overlap >= CONFIG.stabilityThreshold) {
      consecutiveStable++;
      if (consecutiveStable >= CONFIG.consecutiveStableNeeded) return 'suggest_stop';
    } else {
      consecutiveStable = 0;
    }
    return 'continue';
  }

  function getProgress() {
    if (isContinuation) {
      const contSwipes = swipeCount - continuationSwipeStart;
      const t = Math.min(contSwipes / CONTINUATION_MAX_SWIPES, 1);
      return Math.round(Math.pow(t, 0.6) * 100);
    }
    const t = Math.min(swipeCount / 18, 1);
    return Math.round(Math.pow(t, 0.6) * 100);
  }

  function getFinalResults() {
    // Liked SKUs first — exclude GZQB if user said no to sleeves
    const likedSkus = likedIndices.map(i => pmiData.skus[i]).filter(s => {
      if (!products[s]) return false;
      if (sleevePreference === false && s.startsWith('GZQB')) return false;
      return true;
    });

    // Recommendations: top scored minus shown
    const allScored = [];
    for (let i = 0; i < pmiData.skus.length; i++) {
      if (shownIndices.includes(i)) continue;
      const sku = pmiData.skus[i];
      if (!products[sku]) continue;
      // Exclude sleeves from results if user said no
      if (sleevePreference === false && sku.startsWith('GZQB')) continue;
      allScored.push({ idx: i, sku, score: profileVector[i] || 0 });
    }
    allScored.sort((a, b) => b.score - a.score);

    const recoSlots = CONFIG.totalResultSlots;
    const newSkuSet = new Set(CONFIG.newSkus);

    // Separate new and regular
    const newRecs = allScored.filter(s => newSkuSet.has(s.sku));
    const regularRecs = allScored.filter(s => !newSkuSet.has(s.sku));

    // Take regular recs
    const regularCount = Math.max(0, recoSlots - CONFIG.newSlotsCount);
    const selectedRegular = regularRecs.slice(0, regularCount);

    // Score new arrivals by affinity with user profile:
    // For each new SKU, compute average profile score of its size-prefix peers
    // so a tribal lover gets new GZQB designs, a minimalist gets new SPX designs
    const prefixScores = {};
    for (const s of allScored) {
      const prefix = s.sku.split('-')[0];
      if (!prefixScores[prefix]) prefixScores[prefix] = { sum: 0, count: 0 };
      prefixScores[prefix].sum += s.score;
      prefixScores[prefix].count++;
    }
    for (const nr of newRecs) {
      const prefix = nr.sku.split('-')[0];
      const ps = prefixScores[prefix];
      // Blend: own PMI score (if any) + prefix affinity average
      const prefixAvg = ps ? ps.sum / ps.count : 0;
      nr.score = nr.score + prefixAvg;
    }
    newRecs.sort((a, b) => b.score - a.score);

    // Take top new arrivals by affinity
    const selectedNew = newRecs.slice(0, CONFIG.newSlotsCount);

    // Ensure minimum 4 GZQB in total results if user liked at least one GZQB
    const likedGZQB = likedSkus.filter(s => s.startsWith('GZQB'));
    const MIN_GZQB = 4;
    if (likedGZQB.length > 0) {
      const gzqbInRegular = selectedRegular.filter(s => s.sku.startsWith('GZQB'));
      const gzqbInNew = selectedNew.filter(s => s.sku.startsWith('GZQB'));
      const totalGZQB = likedGZQB.length + gzqbInRegular.length + gzqbInNew.length;
      if (totalGZQB < MIN_GZQB) {
        const needed = MIN_GZQB - totalGZQB;
        const existingSkus = new Set([
          ...likedSkus,
          ...selectedRegular.map(s => s.sku),
          ...selectedNew.map(s => s.sku)
        ]);
        // Pull extra GZQB from allScored that aren't already selected
        const extraGZQB = allScored
          .filter(s => s.sku.startsWith('GZQB') && !existingSkus.has(s.sku))
          .slice(0, needed);
        selectedRegular.push(...extraGZQB);
      }
    }

    // Merge and scatter new arrivals
    const combined = [...selectedRegular];
    const newPositions = [];
    const step = Math.max(1, Math.floor(combined.length / (selectedNew.length + 1)));
    for (let i = 0; i < selectedNew.length; i++) {
      const pos = Math.min((i + 1) * step, combined.length);
      newPositions.push(pos + i); // offset for previous insertions
    }
    for (let i = 0; i < selectedNew.length; i++) {
      combined.splice(newPositions[i], 0, selectedNew[i]);
    }

    // Sold Out faux-scarcity: take 5 SKUs just below the cutoff (close PMI but not top 40)
    const selectedSkuSet = new Set([
      ...likedSkus,
      ...combined.map(s => s.sku)
    ]);
    const soldOutCandidates = allScored
      .filter(s => !selectedSkuSet.has(s.sku) && !newSkuSet.has(s.sku) && s.score > 0)
      .slice(0, 5)
      .map(s => s.sku);

    // Scatter sold out items at random positions within combined
    const soldOutPositions = [];
    const combinedLen = combined.length;
    while (soldOutPositions.length < soldOutCandidates.length && soldOutPositions.length < combinedLen) {
      const pos = Math.floor(Math.random() * combinedLen);
      if (!soldOutPositions.includes(pos)) soldOutPositions.push(pos);
    }
    soldOutPositions.sort((a, b) => a - b);
    for (let i = 0; i < soldOutPositions.length; i++) {
      combined.splice(soldOutPositions[i] + i, 0, { sku: soldOutCandidates[i], soldOut: true });
    }

    return {
      favorites: likedSkus,
      recommendations: combined.map(s => typeof s === 'object' && s.sku ? s.sku : s),
      soldOutSkus: new Set(soldOutCandidates)
    };
  }

  // ==================== SWIPE UI ====================
  function createCard(skuIndex, isTop) {
    const sku = pmiData.skus[skuIndex];
    const product = products[sku];
    if (!product) return null;

    const card = document.createElement('div');
    card.className = `swipe-card ${isTop ? 'top' : 'behind'}`;
    card.dataset.skuIndex = skuIndex;
    card.innerHTML = `
      <img src="${cdnResize(product.img, 600)}" alt="${product.t}" loading="eager" fetchpriority="high" decoding="async">
      <div class="card-overlay nope-overlay"><span class="overlay-label">NOPE</span></div>
      <div class="card-overlay like-overlay"><span class="overlay-label">LIKE</span></div>
    `;
    return card;
  }

  function loadNextCards() {
    const stack = document.getElementById('card-stack');
    // Remove old flash overlays
    stack.querySelectorAll('.flash-overlay').forEach(f => f.remove());

    // Clear existing cards
    stack.innerHTML = '';

    // Get next 2 SKUs
    const nextIdx = selectNextSku();
    if (nextIdx < 0) { finishSwipes(); return; }

    // Behind card (next next) — temporarily mark nextIdx to avoid duplicate
    const origBehind = behindCardIndex;
    behindCardIndex = nextIdx;
    const nextNextIdx = selectNextSku();
    behindCardIndex = nextNextIdx >= 0 ? nextNextIdx : -1;

    if (nextNextIdx >= 0) {
      const behindCardEl = createCard(nextNextIdx, false);
      if (behindCardEl) stack.appendChild(behindCardEl);
    }

    // Top card
    const topCard = createCard(nextIdx, true);
    if (topCard) {
      stack.appendChild(topCard);
      attachDragListeners(topCard);
    }
  }

  function attachDragListeners(card) {
    card.addEventListener('touchstart', onDragStart, { passive: true });
    card.addEventListener('mousedown', onDragStart);
    document.addEventListener('touchmove', onDragMove, { passive: false });
    document.addEventListener('mousemove', onDragMove);
    document.addEventListener('touchend', onDragEnd);
    document.addEventListener('mouseup', onDragEnd);
  }

  function onDragStart(e) {
    if (quizFinished || isAnimating) return;
    const touch = e.touches ? e.touches[0] : e;
    isDragging = true;
    startX = touch.clientX;
    startY = touch.clientY;
    currentX = 0;
    currentCard = e.currentTarget;
    swipeStartTime = Date.now();
    currentCard.style.transition = 'none';
  }

  function onDragMove(e) {
    if (!isDragging || !currentCard) return;
    if (e.cancelable) e.preventDefault();
    const touch = e.touches ? e.touches[0] : e;
    currentX = touch.clientX - startX;
    const rotation = currentX * 0.08;
    currentCard.style.transform = `translateX(${currentX}px) rotate(${rotation}deg)`;

    // Show overlays
    const likeOverlay = currentCard.querySelector('.like-overlay');
    const nopeOverlay = currentCard.querySelector('.nope-overlay');
    const progress = Math.min(Math.abs(currentX) / CONFIG.swipeThreshold, 1);

    if (currentX > 0) {
      likeOverlay.style.opacity = progress;
      nopeOverlay.style.opacity = 0;
    } else {
      nopeOverlay.style.opacity = progress;
      likeOverlay.style.opacity = 0;
    }
  }

  function onDragEnd(e) {
    if (!isDragging || !currentCard) return;
    isDragging = false;
    const dwellTime = Date.now() - swipeStartTime;

    if (Math.abs(currentX) > CONFIG.swipeThreshold) {
      const isLike = currentX > 0;
      executeSwipe(isLike, dwellTime);
    } else {
      // Snap back
      currentCard.style.transition = 'transform 0.3s ease';
      currentCard.style.transform = 'translateX(0) rotate(0deg)';
      currentCard.querySelector('.like-overlay').style.opacity = 0;
      currentCard.querySelector('.nope-overlay').style.opacity = 0;
    }
    currentCard = null;
  }

  function executeSwipe(isLike, dwellTime) {
    if (isAnimating) return;
    const card = document.querySelector('.swipe-card.top');
    if (!card) return;
    isAnimating = true;
    // Safety: unlock after 600ms in case animationend never fires
    setTimeout(() => { isAnimating = false; }, 600);
    const skuIndex = parseInt(card.dataset.skuIndex);

    // Animate out
    card.style.transition = '';
    card.classList.add(isLike ? 'swiping-right' : 'swiping-left');

    // Preload the next 2-3 likely candidates to warm the image cache
    if (swipeCount >= 2 && swipeCount % 3 === 0) {
      const warmIdxs = [];
      for (let i = 0; i < pmiData.skus.length && warmIdxs.length < 4; i++) {
        if (shownIndices.includes(i) || warmIdxs.includes(i)) continue;
        const sku = pmiData.skus[i];
        if (products[sku]) warmIdxs.push(sku);
      }
      preloadSkus(warmIdxs, 600);
    }

    // Update state
    shownIndices.push(skuIndex);
    if (isLike) likedIndices.push(skuIndex);
    const swipedSku = pmiData.skus[skuIndex];
    if (swipedSku.startsWith('GZQB')) {
      sleevesShown++;
      if (isLike) sleevesLiked++;
    }
    updateProfile(skuIndex, isLike);
    swipeCount++;

    // Auto-detect sleeve preference after enough GZQB shown
    if (!sleeveQuestionShown && sleevesShown >= CONFIG.sleeveCalibrationCount) {
      sleevePreference = sleevesLiked >= CONFIG.sleeveDetectionThreshold;
      sleeveQuestionShown = true;
      if (sleevePreference === false) sleevesShown = CONFIG.minSleeves; // skip future sleeves
      TRACKER.event('sleeve_auto_detected', {
        preference: sleevePreference ? 'yes' : 'no',
        sleeves_shown: sleevesShown,
        sleeves_liked: sleevesLiked,
        swipe_count: swipeCount,
      });
      trackEvent('sleeve_preference', { likes_sleeves: sleevePreference });
    }

    // Update progress
    const progress = getProgress();
    document.getElementById('progress-fill').style.width = progress + '%';
    document.getElementById('progress-text').textContent = progress + '%';

    // Promote behind card
    const behindCard = document.querySelector('.swipe-card.behind');
    if (behindCard) {
      behindCard.classList.remove('behind');
      behindCard.classList.add('top', 'promote');
      attachDragListeners(behindCard);
    }

    // Check stop criterion
    const stopResult = checkStopCriterion();

    // After swipe-out animation, load next cards
    card.addEventListener('animationend', () => {
      card.remove();
      isAnimating = false;

      // If previous swipe flagged pending finish, end now
      if (pendingFinish) {
        finishSwipes();
        return;
      }

      if (stopResult === 'force_stop' || stopResult === 'suggest_stop') {
        // Profile stable — let user swipe the current visible card, then end
        pendingFinish = true;
        // Don't load a new behind card — current top card is the last one
        return;
      }

      // Load next behind card
      const nextIdx = selectNextSku();
      if (nextIdx < 0) { finishSwipes(); return; }

      behindCardIndex = nextIdx; // Track to avoid duplicates
      const stack = document.getElementById('card-stack');
      const newBehind = createCard(nextIdx, false);
      if (newBehind) stack.insertBefore(newBehind, stack.firstChild);
    }, { once: true });

    // Track event (for future backend)
    trackEvent('swipe', {
      sku: pmiData.skus[skuIndex],
      direction: isLike ? 'right' : 'left',
      dwellTime,
      rank: swipeCount,
      progress
    });
  }

  // Sleeve preference is now auto-detected from calibration swipes. No overlay.
  let sleevesToForce = 0; // Kept for backward compat; unused in new flow.
  function setupSleeveQuestion() { /* no-op — kept for init() compatibility */ }

  function finishSwipes() {
    quizFinished = true;
    document.getElementById('progress-fill').style.width = '100%';
    document.getElementById('progress-text').textContent = '100%';
    TRACKER.event('swipes_finished', { total_swipes: swipeCount, liked: likedIndices.length, continuation: TRACKER.continuation });

    // Flush buffered swipes to backend
    TRACKER.flushSwipes();

    if (isContinuation) {
      // Skip survey & profile page — go straight to loading then results
      setTimeout(() => {
        showScreen('screen-loading');
        startContinuationLoading();
      }, 500);
    } else {
      setTimeout(() => showScreen('screen-survey'), 500);
    }
  }

  function startContinuationLoading() {
    loadingStartTime = Date.now();
    TRACKER.event('loading_started', { type: 'continuation', continuation: TRACKER.continuation });
    const messages = document.querySelectorAll('.loading-msg');
    const totalDuration = 4000; // halved from 8000
    const stepDuration = totalDuration / messages.length;

    // Reset loading messages
    messages.forEach(m => { m.classList.remove('visible'); m.classList.remove('done'); });

    // Reset ring
    const ring = document.querySelector('.ring-progress');
    if (ring) {
      ring.style.transition = 'none';
      const circumference = 2 * Math.PI * 90;
      ring.style.strokeDashoffset = circumference + '';
      ring.offsetHeight; // force reflow
      ring.style.transition = `stroke-dashoffset ${totalDuration}ms ease-in-out`;
      setTimeout(() => { ring.style.strokeDashoffset = '0'; }, 50);
    }

    // Reset and play video (iOS-safe — see restartLoadingVideo helper).
    const video = document.querySelector('.loading-video');
    if (video) {
      restartLoadingVideo(video);
    }

    // Update loading testimonials based on saved survey reasons
    buildLoadingTestimonials();

    // Step through messages (same as original)
    messages.forEach((msg, i) => {
      setTimeout(() => {
        messages.forEach(m => { m.classList.remove('visible'); m.classList.add('done'); });
        msg.classList.remove('done');
        msg.classList.add('visible');
      }, i * stepDuration);
    });

    // After loading, build results and show directly (skip profile page)
    setTimeout(() => {
      TRACKER.event('loading_completed', { type: 'continuation', duration_ms: Date.now() - loadingStartTime, continuation: TRACKER.continuation });
      buildResults();
      showScreen('screen-results');
      window.scrollTo(0, 0);
    }, totalDuration);
  }

  // Button swipes
  function setupSwipeUI() {
    document.getElementById('btn-nope').addEventListener('click', () => {
      if (!quizFinished) { TRACKER.event('button_nope_clicked', { swipe_count: swipeCount }); executeSwipe(false, 0); }
    });
    document.getElementById('btn-like').addEventListener('click', () => {
      if (!quizFinished) { TRACKER.event('button_like_clicked', { swipe_count: swipeCount }); executeSwipe(true, 0); }
    });
  }

  // ==================== SURVEY ====================
  function updateSurveyProgress(percent) {
    document.getElementById('survey-progress-fill').style.width = percent + '%';
    document.getElementById('survey-progress-text').textContent = percent + '%';
  }

  function setupSurveyUI() {
    // Q1: Single select - auto advance
    document.querySelectorAll('#survey-q1 .survey-option').forEach(btn => {
      btn.addEventListener('click', () => {
        document.querySelectorAll('#survey-q1 .survey-option').forEach(b => b.classList.remove('selected'));
        btn.classList.add('selected');
        surveyData.realTattoos = btn.dataset.value;
        TRACKER.event('survey_q1_tattoo', { value: btn.dataset.value });
        setTimeout(() => {
          document.getElementById('survey-q1').classList.remove('active');
          document.getElementById('survey-q2').classList.add('active');
          updateSurveyProgress(88);
        }, 300);
      });
    });

    // Q2: Single select with auto-advance. "Other" reveals a text input + continue button.
    const q2AdvanceToQ3 = () => {
      document.getElementById('survey-q2').classList.remove('active');
      document.getElementById('survey-q3').classList.add('active');
      updateSurveyProgress(95);
    };

    const showOtherInput = (show) => {
      // Prefer new liquid (.survey-other-wrapper). Fall back to old liquid (.survey-other-input).
      const wrap = document.getElementById('other-wrapper');
      const input = document.getElementById('other-input');
      const oldContinue = document.getElementById('btn-q2-continue');
      if (wrap) {
        wrap.classList.toggle('hidden', !show);
      } else if (input) {
        input.classList.toggle('hidden', !show);
        if (oldContinue) oldContinue.classList.toggle('hidden', !show);
      }
    };
    // Hide any continue button + other input initially (for new AND old liquid)
    showOtherInput(false);
    const legacyContinueBtn = document.getElementById('btn-q2-continue');
    if (legacyContinueBtn) legacyContinueBtn.classList.add('hidden');

    document.querySelectorAll('#survey-q2 .survey-option').forEach(btn => {
      btn.addEventListener('click', () => {
        document.querySelectorAll('#survey-q2 .survey-option').forEach(b => b.classList.remove('selected'));
        btn.classList.add('selected');
        const value = btn.dataset.value;
        surveyData.reasons = [value];
        TRACKER.event('survey_q2_reason_selected', { reason: value });

        if (value === 'other') {
          showOtherInput(true);
          setTimeout(() => {
            const input = document.getElementById('other-input');
            if (input) input.focus();
          }, 100);
        } else {
          showOtherInput(false);
          surveyData.otherText = '';
          TRACKER.event('survey_q2_continue', { reasons: surveyData.reasons, other_text: null });
          setTimeout(q2AdvanceToQ3, 250);
        }
      });
    });

    // Wire up whichever Continue button exists (new or old liquid)
    const otherContinueBtn = document.getElementById('btn-q2-other-continue') || legacyContinueBtn;
    if (otherContinueBtn) {
      otherContinueBtn.addEventListener('click', () => {
        const input = document.getElementById('other-input');
        const txt = input ? (input.value || '').trim() : '';
        surveyData.otherText = txt;
        TRACKER.event('survey_q2_other_text', { text: txt });
        TRACKER.event('survey_q2_continue', { reasons: surveyData.reasons, other_text: txt });
        q2AdvanceToQ3();
      });
    }
    const otherInput = document.getElementById('other-input');
    if (otherInput) {
      otherInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' && otherContinueBtn) { e.preventDefault(); otherContinueBtn.click(); }
      });
    }

    // Q3: Age slider — disabled until user interacts
    const slider = document.getElementById('age-slider');
    const display = document.getElementById('age-display');
    const seeResultsBtn = document.getElementById('btn-see-results');
    seeResultsBtn.disabled = true;
    seeResultsBtn.style.opacity = '0.4';

    let ageTracked = false;
    slider.addEventListener('input', () => {
      display.textContent = slider.value;
      surveyData.age = parseInt(slider.value);
      seeResultsBtn.disabled = false;
      seeResultsBtn.style.opacity = '1';
      if (!ageTracked) { TRACKER.event('survey_q3_age_started', {}); ageTracked = true; }
    });
    slider.addEventListener('change', () => {
      TRACKER.event('survey_q3_age_set', { age: parseInt(slider.value) });
    });

    seeResultsBtn.addEventListener('click', () => {
      if (seeResultsBtn.disabled) return;
      TRACKER.event('see_results_clicked', { age: surveyData.age, reasons: surveyData.reasons, real_tattoos: surveyData.realTattoos });
      trackEvent('survey_complete', surveyData);
      // Send survey data to backend
      TRACKER.sendSurvey(
        sleevePreference === true ? 'yes' : sleevePreference === false ? 'no' : null,
        surveyData.realTattoos,
        surveyData.reasons,
        String(surveyData.age),
        surveyData.otherText || null
      );
      if (isRetake) {
        // Skip loading and profile screens on retake
        buildResults();
        showScreen('screen-results');
      } else {
        showScreen('screen-loading');
        startLoadingSequence();
      }
    });
  }

  // ==================== LOADING ====================
  // Robust video restart that works on iOS Safari (which often gets stuck on
  // the last frame of an already-autoplayed video).
  function restartLoadingVideo(video) {
    video.pause();
    const baseSrc = (video.getAttribute('src') || video.querySelector('source')?.getAttribute('src') || '').split('?')[0];
    if (baseSrc) {
      video.removeAttribute('src');
      while (video.firstChild) video.removeChild(video.firstChild);
      video.load();
      video.setAttribute('src', baseSrc + '?t=' + Date.now());
      video.load();
    }
    video.muted = true;
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    video.playbackRate = 1.0;

    let playAttempts = 0;
    const tryPlay = () => {
      try { video.currentTime = 0; } catch (e) {}
      const p = video.play();
      if (p && typeof p.then === 'function') {
        p.catch(() => {
          playAttempts++;
          if (playAttempts < 5) setTimeout(tryPlay, 100);
        });
      }
    };

    // Kick off play as soon as the video has enough data
    if (video.readyState >= 2) {
      tryPlay();
    } else {
      video.addEventListener('loadeddata', tryPlay, { once: true });
      video.addEventListener('canplay', tryPlay, { once: true });
      // Also attempt immediately in case events already fired
      setTimeout(tryPlay, 50);
    }
  }

  let loadingStartTime = 0;
  function startLoadingSequence() {
    loadingStartTime = Date.now();
    TRACKER.event('loading_started', { type: 'first_time' });
    const messages = document.querySelectorAll('.loading-msg');
    const totalDuration = 6000;
    const stepDuration = totalDuration / messages.length;

    // Preload result images NOW so they're ready when profile/results appear
    try {
      const preliminaryResults = getFinalResults();
      preloadSkus(preliminaryResults.favorites.slice(0, 8), 400);
      preloadSkus(preliminaryResults.recommendations.slice(0, 12), 400);
    } catch (e) {}

    // Reset and play video. On iOS Safari, the video sometimes sticks on its
    // last frame because it was already autoplayed off-screen. We forcibly
    // reload, wait for data to be ready, then play. Retry on failure.
    const video = document.querySelector('.loading-video');
    if (video) {
      restartLoadingVideo(video);
    }

    // Reset ring animation
    const ring = document.querySelector('.ring-progress');
    if (ring) {
      ring.style.transition = 'none';
      const circumference = 2 * Math.PI * 90;
      ring.style.strokeDashoffset = circumference + '';
      ring.offsetHeight;
      ring.style.transition = `stroke-dashoffset ${totalDuration}ms ease-in-out`;
      setTimeout(() => { ring.style.strokeDashoffset = '0'; }, 50);
    }

    // Reset loading messages
    messages.forEach(m => { m.classList.remove('visible'); m.classList.remove('done'); });

    messages.forEach((msg, i) => {
      // Show this message
      setTimeout(() => {
        messages.forEach(m => { m.classList.remove('visible'); m.classList.add('done'); });
        msg.classList.remove('done');
        msg.classList.add('visible');
      }, i * stepDuration);
    });

    // Build testimonials
    buildLoadingTestimonials();

    // After full duration, show profile reveal
    setTimeout(() => {
      messages.forEach(m => { m.classList.add('done'); m.classList.remove('visible'); });
      TRACKER.event('loading_completed', { type: 'first_time', duration_ms: Date.now() - loadingStartTime });
      buildResults();
      showScreen('screen-profile');
      animateProfileStat();
    }, totalDuration);
  }

  function buildLoadingTestimonials() {
    const container = document.getElementById('loading-testimonials');
    const reviews = getReviewsForReasons(surveyData.reasons);
    const starSvg = `<svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg"><rect width="32" height="32" fill="#00B67A"/><g clip-path="url(#c)"><path d="M9.2 27l6.8-5.193L22.797 27l-2.595-8.404L27 13.403h-8.402L16.001 5l-2.597 8.403H5l6.798 5.193L9.203 27z" fill="#fff"/></g><defs><clipPath id="c"><rect width="22" height="22" fill="#fff" transform="translate(5 5)"/></clipPath></defs></svg>`;

    const slides = reviews.map((review, i) => `
      <div class="swiper-slide">
        <div class="lt-media">
          <img src="${cdnResize(CONFIG.testimonialImages[i % CONFIG.testimonialImages.length], 500)}" alt="" loading="eager" decoding="async" fetchpriority="high">
        </div>
        <div class="lt-content">
          <div class="lt-text"><p>${review.text}</p></div>
          <div>
            <div class="lt-stars-wrapper">
              <div class="lt-stars">${starSvg.repeat(5)}</div>
              <span class="lt-badge">Verified Customer</span>
            </div>
            <p class="lt-author">${review.name}</p>
          </div>
        </div>
      </div>
    `).join('');

    const halfStar = `<svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg"><defs><linearGradient id="halfL"><stop offset="50%" stop-color="#00B67A"/><stop offset="50%" stop-color="#ddd"/></linearGradient></defs><rect width="32" height="32" fill="url(#halfL)"/><g clip-path="url(#c)"><path d="M9.2 27l6.8-5.193L22.797 27l-2.595-8.404L27 13.403h-8.402L16.001 5l-2.597 8.403H5l6.798 5.193L9.203 27z" fill="#fff"/></g><defs><clipPath id="c"><rect width="22" height="22" fill="#fff" transform="translate(5 5)"/></clipPath></defs></svg>`;

    container.innerHTML = `
      <div class="rst-header" style="margin-bottom:20px;">
        <p class="rst-review-text">100,000+ Happy Customers</p>
        <div class="rst-stars-top">
          <div class="lt-stars">${starSvg.repeat(4)}${halfStar}</div>
          <span class="rst-stars-count">Excellent 4.5</span>
        </div>
        <div class="rst-bottom-text" style="margin-top:12px;">100-Day Money Back Guarantee</div>
      </div>
      <div class="swiper lt-swiper">
        <div class="swiper-wrapper">${slides}</div>
      </div>
      <div class="lt-nav">
        <button class="lt-btn lt-btn-prev"><span class="arrow-icon"></span></button>
        <button class="lt-btn lt-btn-next"><span class="arrow-icon"></span></button>
      </div>
    `;

    // Init Swiper (same config as ss-testimonial-28)
    new Swiper('.lt-swiper', {
      speed: 300,
      loop: true,
      navigation: {
        nextEl: '.lt-btn-next',
        prevEl: '.lt-btn-prev',
      },
      breakpoints: {
        320: { spaceBetween: 4, slidesPerView: 1.7 },
        768: { spaceBetween: 20, slidesPerView: 4 },
      }
    });
  }

  function getReviewsForReasons(reasons) {
    const allReviews = {
      try_before_real: [
        { name: 'Marcus T.', text: "I'd been overthinking my first tattoo for 3 years straight. Wore INKON for 2 weeks to test the design. Now I know exactly what I want permanently. No more scrolling Pinterest at 2am." },
        { name: 'Ryan K.', text: "Tested 3 different placements before committing to real ink. Turns out my original idea was way too small for my forearm. INKON literally saved me from a $400 mistake." },
        { name: 'Dylan W.', text: "My girlfriend thought it was real for 4 days. My tattoo artist friend couldn't tell either. Getting the actual piece done next month — no hesitation this time." },
        { name: 'Adrian P.', text: "I work in finance so I needed to see if I could pull off a forearm piece that's hidden under a dress shirt. Inked on Friday, clean on Monday. Perfect test drive." },
        { name: 'Jake L.', text: "Spent years with a folder of 200+ saved tattoo ideas on Instagram. INKON let me actually try them on my skin instead of just imagining. Game changer for someone who overthinks everything." }
      ],
      prank_fun: [
        { name: 'Chris M.', text: "Put a full sleeve on before family dinner. My mom almost had a heart attack. Best prank of my life. Took me 20 minutes to convince her it wasn't real." },
        { name: 'Noah B.', text: "My buddies bet me I wouldn't get a neck tattoo. Showed up to the gym with one Monday morning. Their faces were absolutely priceless. Nobody could tell." },
        { name: 'Tyler J.', text: "Spring break in Miami with full sleeves. Everyone at the pool thought I was covered in real ink. Three people asked for my artist's Instagram. Best $15 I ever spent." },
        { name: 'Liam S.', text: "Pranked my entire office with a forearm tattoo. My boss pulled me aside looking concerned. Peeled it off right in front of him. The whole floor was dying laughing." }
      ],
      event_vacation: [
        { name: 'Ethan R.', text: "Got a full arm for a week in Tulum. Sweat, pool, ocean — it survived everything. People kept asking where I got it done. Best vacation accessory I've ever packed." },
        { name: 'Brandon G.', text: "Music festival with INKON sleeves. The amount of people who came up to talk about my tattoos was insane. It's like an instant conversation starter everywhere you go." },
        { name: 'Jordan H.', text: "Bachelor party weekend in Vegas. The whole crew ordered INKON beforehand. We looked absolutely dialed in. Held up through 3 days of pool parties, no problem." }
      ],
      attract_attention: [
        { name: 'Austin C.', text: "Not gonna lie, I wanted to look better shirtless this summer. The confidence boost from rolling up my sleeves is real. People treat you differently when you look like you have your life together." },
        { name: 'Mason D.', text: "Something about having visible tattoos just changes the energy in a room. More eye contact, more conversations, more presence. INKON is a legit confidence cheat code." },
        { name: 'Zach V.', text: "Started wearing INKON to the gym. The difference in how people react is night and day. Strangers ask about the designs, start conversations. It's like wearing a personality on your skin." },
        { name: 'Patrick W.', text: "Updated my dating profile with a photo showing my INKON. The matches jumped noticeably. Studies say tattooed guys are perceived as more confident — I believe it now." }
      ],
      other: [
        { name: 'James F.', text: "The quality is insane. My barber literally asked who did the work. Wore it for 10 days, got compliments every single day. This is nothing like the cheap transfer tattoos I tried as a kid." },
        { name: 'Alex R.', text: "Applied it in 2 minutes, lasted almost 2 weeks. No film, no shine, no weird edges. My coworkers were completely fooled. Even up close it looks like real ink." },
        { name: 'David K.', text: "I was skeptical — $25 for a temp tattoo? But this looks like a real $500 piece. The detail is ridiculous. Already ordered 3 more designs to rotate through." },
        { name: 'Sam B.', text: "Perfect for someone in consulting who can't get real ink but wants the look on weekends. Inked on Friday, clean on Monday. Finally found something that adapts to my life." },
        { name: 'Mike T.', text: "I've wanted a tattoo for years but the permanence freaked me out. INKON let me stop overthinking and just try it. Spoiler: it looks amazing and I feel like a different person wearing it." }
      ]
    };

    let selected = [];
    if (reasons.length === 0 || (reasons.length === 1 && reasons[0] === 'other')) {
      selected = [...allReviews.other];
    } else {
      for (const reason of reasons) {
        if (reason !== 'other' && allReviews[reason]) {
          selected.push(...allReviews[reason].slice(0, 2));
        }
      }
      if (selected.length < 4) {
        selected.push(...allReviews.other.slice(0, 4 - selected.length));
      }
    }
    return selected.slice(0, 6);
  }

  // ==================== RESULTS ====================
  // Cache for inventory checks (SKU -> bool available)
  const inventoryCache = {};

  // Check if a SKU has any available variant. Uses /products/{handle}.js which is fast + small.
  async function isSkuAvailable(sku) {
    if (sku in inventoryCache) return inventoryCache[sku];
    const p = products[sku];
    if (!p || !p.h) { inventoryCache[sku] = true; return true; } // assume available if no handle
    try {
      const res = await fetch(`/products/${p.h}.js`, { cache: 'no-store' });
      if (!res.ok) { inventoryCache[sku] = true; return true; }
      const data = await res.json();
      const variants = data.variants || [];
      // Available if any variant is available OR has positive inventory
      const available = variants.some(v => {
        if (v.available === false) return false;
        if (typeof v.inventory_quantity === 'number') return v.inventory_quantity > 0;
        return v.available === true;
      });
      inventoryCache[sku] = available;
      return available;
    } catch (e) {
      inventoryCache[sku] = true; // fail-open: show rather than hide
      return true;
    }
  }

  async function buildResults() {
    const results = getFinalResults();

    // Filter favorites: remove any that are truly sold out in Shopify inventory
    const availabilityResults = await Promise.all(
      results.favorites.map(sku => isSkuAvailable(sku).then(ok => ({ sku, ok })))
    );
    const removedFavs = availabilityResults.filter(r => !r.ok).map(r => r.sku);
    results.favorites = availabilityResults.filter(r => r.ok).map(r => r.sku);
    if (removedFavs.length) {
      TRACKER.event('favorites_soldout_filtered', { removed: removedFavs, remaining: results.favorites.length });
    }

    // Track results to backend
    const soldOutArr = [...(results.soldOutSkus || [])];
    const newInResults = results.recommendations.filter(s => CONFIG.newSkus.includes(s));
    TRACKER.sendResults(results.favorites, results.recommendations, soldOutArr, newInResults);

    const favGrid = document.getElementById('favorites-grid');
    const recoGrid = document.getElementById('recommendations-grid');

    const newSkuSet = new Set(CONFIG.newSkus);

    // Favorites
    if (results.favorites.length > 0) {
      favGrid.innerHTML = results.favorites.map(sku => buildProductCard(sku, false)).join('');
    } else {
      document.querySelector('.results-header').style.display = 'none';
      favGrid.style.display = 'none';
    }

    // Recommendations
    recoGrid.innerHTML = results.recommendations.map(sku => {
      if (results.soldOutSkus.has(sku)) return buildSoldOutCard(sku);
      return buildProductCard(sku, newSkuSet.has(sku));
    }).join('');

    // Add to cart listeners
    document.querySelectorAll('.btn-add-to-cart').forEach(btn => {
      btn.addEventListener('click', () => addToBundle(btn));
    });

    // Init videos Swiper
    const vidSwiper = new Swiper('.videos-swiper', {
      speed: 300,
      loop: false,
      freeMode: true,
      grabCursor: true,
      breakpoints: {
        320: { spaceBetween: 24, slidesPerView: 1.7 },
        768: { spaceBetween: 24, slidesPerView: 5 },
      }
    });

    // Play any video that becomes visible inside the carousel.
    // Previously only the activeIndex was played — with slidesPerView > 1,
    // the last slide never became activeIndex so it never played.
    const videoCards = document.querySelectorAll('.videos-swiper .video-card');
    videoCards.forEach(card => {
      const v = card.querySelector('video');
      if (!v) return;
      v.muted = true;
      v.setAttribute('playsinline', '');
      v.setAttribute('webkit-playsinline', '');
    });

    const cardObs = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        const v = e.target.querySelector('video');
        if (!v) return;
        if (e.isIntersecting) {
          v.muted = true;
          v.play().catch(() => {});
        } else {
          v.pause();
        }
      });
    }, { threshold: 0.5 });
    videoCards.forEach(card => cardObs.observe(card));

    // Pause all when the whole section leaves viewport
    const vidSection = document.querySelector('.videos-swiper');
    if (vidSection) {
      const sectionObs = new IntersectionObserver((entries) => {
        entries.forEach(e => {
          if (!e.isIntersecting) {
            document.querySelectorAll('.videos-swiper video').forEach(v => v.pause());
          }
        });
      }, { threshold: 0 });
      sectionObs.observe(vidSection);
    }

    // Init results testimonials (dynamic based on survey answers)
    initResultsTestimonials();

    // Init results FAQ
    initResultsFaq();
  }

  function initResultsTestimonials() {
    const container = document.getElementById('results-testimonials');
    const reviews = getReviewsForReasons(surveyData.reasons);
    const starSvg = `<svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg"><rect width="32" height="32" fill="#00B67A"/><g clip-path="url(#c)"><path d="M9.2 27l6.8-5.193L22.797 27l-2.595-8.404L27 13.403h-8.402L16.001 5l-2.597 8.403H5l6.798 5.193L9.203 27z" fill="#fff"/></g><defs><clipPath id="c"><rect width="22" height="22" fill="#fff" transform="translate(5 5)"/></clipPath></defs></svg>`;
    const halfStar = `<svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg"><defs><linearGradient id="halfR"><stop offset="50%" stop-color="#00B67A"/><stop offset="50%" stop-color="#ddd"/></linearGradient></defs><rect width="32" height="32" fill="url(#halfR)"/><g clip-path="url(#c)"><path d="M9.2 27l6.8-5.193L22.797 27l-2.595-8.404L27 13.403h-8.402L16.001 5l-2.597 8.403H5l6.798 5.193L9.203 27z" fill="#fff"/></g><defs><clipPath id="c"><rect width="22" height="22" fill="#fff" transform="translate(5 5)"/></clipPath></defs></svg>`;

    const slides = reviews.map((review, i) => `
      <div class="swiper-slide">
        <div class="lt-media">
          <img src="${cdnResize(CONFIG.testimonialImages[i % CONFIG.testimonialImages.length], 500)}" alt="" loading="lazy" decoding="async">
        </div>
        <div class="lt-content">
          <div class="lt-text"><p>${review.text}</p></div>
          <div>
            <div class="lt-stars-wrapper">
              <div class="lt-stars">${starSvg.repeat(5)}</div>
              <span class="lt-badge">Verified Customer</span>
            </div>
            <p class="lt-author">${review.name}</p>
          </div>
        </div>
      </div>
    `).join('');

    container.innerHTML = `
      <div class="rst-header">
        <p class="rst-review-text">100,000+ Happy Customers</p>
        <div class="rst-stars-top">
          <div class="lt-stars">${starSvg.repeat(4)}${halfStar}</div>
          <span class="rst-stars-count">Excellent 4.5</span>
        </div>
        <h2 class="rst-heading"><strong>REAL</strong> RESULTS</h2>
      </div>
      <div class="rst-slider">
        <div class="swiper rst-swiper">
          <div class="swiper-wrapper">${slides}</div>
        </div>
        <div class="lt-nav">
          <button class="lt-btn lt-btn-prev rst-prev"><span class="arrow-icon"></span></button>
          <button class="lt-btn lt-btn-next rst-next"><span class="arrow-icon"></span></button>
        </div>
      </div>
      <div class="rst-bottom-text">100-Day Money Back Guarantee</div>
    `;

    new Swiper('.rst-swiper', {
      speed: 300,
      loop: true,
      navigation: { nextEl: '.rst-next', prevEl: '.rst-prev' },
      breakpoints: {
        320: { spaceBetween: 4, slidesPerView: 1.7 },
        768: { spaceBetween: 20, slidesPerView: 4 },
      }
    });
  }

  function initResultsFaq() {
    document.querySelectorAll('.faq-toggle').forEach(toggle => {
      toggle.addEventListener('click', (e) => {
        e.stopPropagation();
        const item = toggle.parentElement;
        const answer = item.querySelector(':scope > .faq-answer');
        const icon = toggle.querySelector('.faq-icon');
        if (!answer) return;

        const isActive = item.classList.contains('active');
        const question = toggle.textContent.trim().substring(0, 50);
        TRACKER.event('faq_toggled', { question, action: isActive ? 'close' : 'open' });

        // Close siblings
        const parent = item.parentElement;
        parent.querySelectorAll(':scope > .faq-item.active').forEach(sib => {
          if (sib !== item) {
            const sibAnswer = sib.querySelector(':scope > .faq-answer');
            const sibIcon = sib.querySelector(':scope > .faq-toggle .faq-icon');
            if (sibAnswer) {
              sibAnswer.style.height = sibAnswer.scrollHeight + 'px';
              sibAnswer.offsetHeight;
              sibAnswer.style.height = '0px';
              sibAnswer.style.paddingTop = '0px';
              sibAnswer.style.paddingBottom = '0px';
              sib.classList.remove('active');
              if (sibIcon) sibIcon.classList.remove('active');
              sibAnswer.addEventListener('transitionend', function h(ev) {
                if (ev.propertyName === 'height') { sibAnswer.style.display = 'none'; sibAnswer.removeEventListener('transitionend', h); }
              });
            }
          }
        });

        if (isActive) {
          answer.style.height = answer.scrollHeight + 'px';
          answer.offsetHeight;
          answer.style.height = '0px';
          answer.style.paddingTop = '0px';
          answer.style.paddingBottom = '0px';
          item.classList.remove('active');
          if (icon) icon.classList.remove('active');
          answer.addEventListener('transitionend', function h(ev) {
            if (ev.propertyName === 'height') { answer.style.display = 'none'; answer.removeEventListener('transitionend', h); }
          });
        } else {
          answer.style.display = 'block';
          answer.style.height = '0px';
          const scrollH = answer.scrollHeight;
          answer.offsetHeight;
          answer.style.height = scrollH + 'px';
          item.classList.add('active');
          if (icon) icon.classList.add('active');
          answer.addEventListener('transitionend', function h(ev) {
            if (ev.propertyName === 'height') { answer.style.height = 'auto'; answer.removeEventListener('transitionend', h); }
          });
        }
      });
    });
  }

  function buildProductCard(sku, isNew) {
    const product = products[sku];
    if (!product) return '';
    const price = parseFloat(product.p).toFixed(2);
    return `
      <div class="product-card">
        ${isNew ? '<span class="product-card-new">NEW</span>' : ''}
        <div class="product-card-image" data-handle="${product.h}" data-sku="${sku}" onclick="window._openPopup(this.dataset.handle, this.dataset.sku)">
          <img src="${cdnResize(product.img, 400)}" alt="${product.t}" loading="lazy" decoding="async">
        </div>
        <div class="product-card-info">
          <div class="product-card-title">${product.t}</div>
          <div class="product-card-price">$${price}</div>
        </div>
        <div class="product-card-actions">
          <button class="btn-add-to-cart" data-handle="${product.h}" data-sku="${sku}">
            <svg viewBox="0 0 24 24" stroke-linecap="round" stroke-linejoin="round">
              <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/>
              <line x1="3" y1="6" x2="21" y2="6"/>
              <path d="M16 10a4 4 0 01-8 0"/>
            </svg>
            ADD TO CART
          </button>
        </div>
      </div>
    `;
  }

  function buildSoldOutCard(sku) {
    const product = products[sku];
    if (!product) return '';
    const price = parseFloat(product.p).toFixed(2);
    return `
      <div class="product-card product-card-soldout">
        <span class="product-card-soldout-tag">SOLD OUT</span>
        <div class="product-card-image product-card-image-soldout">
          <img src="${cdnResize(product.img, 400)}" alt="${product.t}" loading="lazy" decoding="async">
        </div>
        <div class="product-card-info">
          <div class="product-card-title">${product.t}</div>
          <div class="product-card-price">$${price}</div>
        </div>
        <div class="product-card-actions">
          <button class="btn-add-to-cart btn-soldout" disabled>
            SOLD OUT
          </button>
        </div>
      </div>
    `;
  }

  // ==================== BUNDLE SYSTEM ====================
  const bundle = []; // { sku, handle, title, price, img, qty, variantId }

  function addToBundle(btn) {
    const handle = btn.dataset.handle;
    const sku = btn.dataset.sku;
    const product = products[sku];
    if (!product) return;

    // Check if already in bundle
    const existing = bundle.find(b => b.sku === sku);
    if (existing) {
      existing.qty++;
    } else {
      const newItem = {
        sku, handle,
        title: product.t,
        price: parseFloat(product.p),
        img: product.img,
        qty: 1,
        variantId: null
      };
      bundle.push(newItem);
      // Pre-fetch variant ID in background so checkout is instant later
      fetch(`/products/${handle}.json`)
        .then(r => r.json())
        .then(data => { newItem.variantId = data.product.variants[0].id; })
        .catch(() => {});
    }

    renderCardButton(sku);

    trackEvent('add_to_cart', { sku, handle });
    TRACKER.sendBundleEvent('add', sku, parseFloat(product.p), 1);
    updateBundleFooter();
  }

  function renderCardButton(sku) {
    const item = bundle.find(b => b.sku === sku);
    const buttons = document.querySelectorAll(`.btn-add-to-cart[data-sku="${sku}"], .card-qty-control[data-sku="${sku}"]`);
    buttons.forEach(el => {
      const container = el.parentElement;
      if (item && item.qty > 0) {
        // Replace with qty control
        container.innerHTML = `
          <div class="card-qty-control" data-sku="${sku}">
            <button class="card-qty-btn" data-sku="${sku}" data-action="minus">−</button>
            <span class="card-qty-value">${item.qty}</span>
            <button class="card-qty-btn" data-sku="${sku}" data-action="plus">+</button>
          </div>`;
        bindCardQtyButtons(container);
      } else {
        // Revert to ADD button
        const product = products[sku];
        if (!product) return;
        container.innerHTML = `
          <button class="btn-add-to-cart" data-handle="${product.h}" data-sku="${sku}">
            <svg viewBox="0 0 24 24" stroke-linecap="round" stroke-linejoin="round"><path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 01-8 0"/></svg> ADD TO CART
          </button>`;
        const newBtn = container.querySelector('.btn-add-to-cart');
        newBtn.addEventListener('click', () => addToBundle(newBtn));
      }
    });
  }

  function bindCardQtyButtons(container) {
    container.querySelectorAll('.card-qty-btn').forEach(qBtn => {
      qBtn.addEventListener('click', () => {
        const s = qBtn.dataset.sku;
        const action = qBtn.dataset.action;
        const item = bundle.find(b => b.sku === s);
        if (!item) return;
        if (action === 'plus') {
          item.qty++;
          TRACKER.sendBundleEvent('add', s, item.price, 1);
        } else {
          item.qty--;
          if (item.qty <= 0) {
            TRACKER.sendBundleEvent('remove', s, item.price, 0);
            bundle.splice(bundle.indexOf(item), 1);
          } else {
            TRACKER.event('bundle_qty_decrease', { sku: s, price: item.price, new_qty: item.qty });
          }
        }
        renderCardButton(s);
        updateBundleFooter();
      });
    });
  }

  function getTotalBundleQty() {
    return bundle.reduce((sum, b) => sum + b.qty, 0);
  }

  function updateBundleFooter() {
    const total = getTotalBundleQty();
    const footer = document.getElementById('bundle-footer');
    if (!footer) return;

    // Show/hide footer
    footer.classList.toggle('visible', total > 0);

    // Update slots
    const slots = footer.querySelectorAll('.bundle-slot');
    slots.forEach((slot, i) => {
      slot.classList.toggle('filled', i < total);
      slot.classList.toggle('free', i >= 2 && i < total);
    });

    // Update text
    const textEl = footer.querySelector('.bundle-text');
    const confirmBtn = footer.querySelector('.bundle-confirm-btn');
    const countBubble = confirmBtn.querySelector('.bundle-count');
    countBubble.textContent = total;

    // Free count: BUY 2 GET 2 FREE pattern scaling to BUY 4 GET 4 FREE
    // 1-2: 0 free, 3: 1, 4: 2, 5-6: 2, 7: 3, 8: 4
    const freeCount = total < 3 ? 0 : Math.floor(total / 4) * 2 + Math.max(0, Math.min(total % 4 - 2, 2));

    // Next milestone: next multiple of 4
    const currentTier = Math.floor(total / 4) * 4;
    const nextTier = currentTier + 4;
    const nextHalf = nextTier / 2;
    const remaining = nextTier - total;

    if (total === 0) {
      textEl.textContent = 'Add 2 tattoos to unlock 2 FREE!';
    } else if (total === 1) {
      textEl.textContent = 'Add 1 more tattoo to unlock 2 FREE!';
    } else if (total === 2) {
      textEl.innerHTML = 'You can now add your <strong>2 FREE tattoos!</strong>';
    } else if (total === 3) {
      textEl.innerHTML = 'Add <strong>1 more FREE tattoo</strong> to complete your bundle!';
    } else if (total % 4 === 0) {
      // Hit a milestone (4, 8, 12...)
      const paid = total / 2;
      textEl.innerHTML = `<strong>Bundle complete!</strong><br>You unlocked <strong>BUY ${paid} GET ${paid} FREE!</strong><br>You can add more tattoos to get <strong>BUY ${nextHalf} GET ${nextHalf} FREE!</strong>`;
    } else {
      // Between milestones
      const paid = currentTier / 2 || 2;
      textEl.innerHTML = `<strong>Bundle complete!</strong><br>Add <strong>${remaining} more</strong> to unlock <strong>BUY ${nextHalf} GET ${nextHalf} FREE!</strong>`;
    }

    // Determine which items are free (cheapest N)
    const freeSkus = new Set();
    if (freeCount > 0) {
      const expanded = [];
      bundle.forEach(item => {
        for (let i = 0; i < item.qty; i++) expanded.push({ sku: item.sku, price: item.price, idx: i });
      });
      expanded.sort((a, b) => a.price - b.price);
      const freePicked = {};
      for (let i = 0; i < freeCount && i < expanded.length; i++) {
        const s = expanded[i].sku;
        freePicked[s] = (freePicked[s] || 0) + 1;
      }
      Object.keys(freePicked).forEach(s => freeSkus.add(s + ':' + freePicked[s]));
    }

    // Update drawer
    const drawerList = footer.querySelector('.bundle-drawer-list');
    drawerList.innerHTML = bundle.map(item => {
      // Check how many of this item are free
      let freeQty = 0;
      freeSkus.forEach(entry => {
        const [s, cnt] = entry.split(':');
        if (s === item.sku) freeQty = parseInt(cnt);
      });
      const priceHTML = freeQty > 0 && freeQty >= item.qty
        ? `<span class="drawer-price-struck">$${item.price.toFixed(2)}</span> <span class="drawer-price-free">FREE</span>`
        : freeQty > 0
        ? `$${item.price.toFixed(2)} <span class="drawer-price-free-note">(${freeQty} FREE)</span>`
        : `$${item.price.toFixed(2)}`;
      return `
      <div class="bundle-drawer-item" data-sku="${item.sku}">
        <img src="${item.img}" alt="${item.title}">
        <div class="bundle-drawer-info">
          <p class="bundle-drawer-title">${item.title}</p>
          <p class="bundle-drawer-price">${priceHTML}</p>
        </div>
        <div class="bundle-drawer-qty">
          <button class="bundle-qty-btn" data-sku="${item.sku}" data-action="minus">−</button>
          <span>${item.qty}</span>
          <button class="bundle-qty-btn" data-sku="${item.sku}" data-action="plus">+</button>
        </div>
      </div>
    `}).join('');

    // Bind qty buttons
    drawerList.querySelectorAll('.bundle-qty-btn').forEach(qBtn => {
      qBtn.addEventListener('click', () => {
        const s = qBtn.dataset.sku;
        const action = qBtn.dataset.action;
        const item = bundle.find(b => b.sku === s);
        if (!item) return;
        if (action === 'plus') {
          item.qty++;
          TRACKER.sendBundleEvent('add', s, item.price, 1);
        } else {
          item.qty--;
          if (item.qty <= 0) {
            TRACKER.sendBundleEvent('remove', s, item.price, 0);
            bundle.splice(bundle.indexOf(item), 1);
          } else {
            TRACKER.event('bundle_qty_decrease', { sku: s, price: item.price, new_qty: item.qty, source: 'drawer' });
          }
        }
        renderCardButton(s);
        updateBundleFooter();
      });
    });
  }

  window.confirmBundle = async function() {
    TRACKER.event('confirm_bundle_clicked', { items: bundle.length, total: bundle.reduce((s, b) => s + b.price * b.qty, 0) });
    const confirmBtn = document.querySelector('.bundle-confirm-btn');
    confirmBtn.disabled = true;
    confirmBtn.querySelector('.bundle-confirm-text').textContent = 'LOADING...';

    try {
      // Resolve any missing variant IDs (most should already be cached from addToBundle)
      await Promise.all(bundle.map(async (item) => {
        if (!item.variantId) {
          const res = await fetch(`/products/${item.handle}.json`);
          const data = await res.json();
          item.variantId = data.product.variants[0].id;
        }
      }));

      // Build updates object: set quantity for bundle items, 0 for everything else in cart
      const updates = {};
      bundle.forEach(item => { updates[item.variantId] = item.qty; });

      // Get current cart, then update in one call (sets quantities, removes non-bundle items)
      const cartRes = await fetch('/cart.js');
      const cart = await cartRes.json();
      cart.items.forEach(i => {
        if (!(i.variant_id in updates)) updates[i.variant_id] = 0;
      });

      // Resolve session id with multiple fallbacks so attribution survives
      // ad-blockers and slow worker responses.
      const sid = TRACKER.sessionId
        || localStorage.getItem('inkon_session_id')
        || '';

      // Single atomic call: quantities + quiz_session_id in cart attributes
      // Phase 1A.5 — also expose predicted_ltv so Shopify order/webhook can pass it to Meta
      const predLtv = (typeof TRACKER.predictedLtv === 'number')
        ? TRACKER.predictedLtv
        : parseFloat(localStorage.getItem('inkon_predicted_ltv') || '0.44') || 0.44;
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          updates,
          attributes: {
            quiz_session_id: sid,
            inkon_user_id: TRACKER.userId || '',
            predicted_ltv: String(predLtv),
            predicted_score_version: 'v2',
          }
        })
      });

      // Snapshot for tracking
      const snapshot = bundle.map(b => ({ sku: b.sku, price: b.price, qty: b.qty }));
      const totalPrice = bundle.reduce((s, b) => s + b.price * b.qty, 0);

      // Use beacon for events fired right before navigation — sendBeacon is the
      // only reliable way to deliver events during page unload.
      TRACKER.beacon('bundle_confirm', {
        sku: null, price: null, quantity: null, bundle_snapshot: snapshot
      });
      TRACKER.beacon('redirect_to_checkout', { items: bundle.length, total: totalPrice });

      // Redirect to checkout
      window.location.href = '/checkout';
    } catch(e) {
      TRACKER.event('checkout_error_redirect_to_cart', { error: e.message || 'unknown' });
      confirmBtn.disabled = false;
      confirmBtn.querySelector('.bundle-confirm-text').textContent = 'CONFIRM MY BUNDLE';
      window.location.href = '/cart';
    }
  }

  // Reset confirm button state when user navigates back
  window.addEventListener('pageshow', function(e) {
    if (e.persisted) {
      TRACKER.event('returned_from_checkout', { bundle_items: bundle.length });
      const confirmBtn = document.querySelector('.bundle-confirm-btn');
      if (confirmBtn) {
        confirmBtn.disabled = false;
        const txt = confirmBtn.querySelector('.bundle-confirm-text');
        if (txt) txt.textContent = 'CONFIRM MY BUNDLE';
      }
    }
  });

  window.toggleBundleDrawer = function() {
    const drawer = document.querySelector('.bundle-drawer');
    const arrow = document.querySelector('.bundle-arrow');
    if (drawer && arrow) {
      const wasOpen = drawer.classList.contains('open');
      drawer.classList.toggle('open');
      arrow.classList.toggle('open');
      TRACKER.event(wasOpen ? 'bundle_drawer_closed' : 'bundle_drawer_opened', { bundle_items: bundle.length });
    }
  }

  // ==================== RETAKE ====================
  let isRetake = false;
  let isContinuation = false;
  let continuationSwipeStart = 0;
  const CONTINUATION_MIN_SWIPES = 10;
  const CONTINUATION_MAX_SWIPES = 15;

  function setupRetake() {
    const retakeBtn = document.getElementById('retake-quiz');
    if (retakeBtn) {
      retakeBtn.addEventListener('click', (e) => {
        e.preventDefault();
        startContinuation();
      });
    }
    // Track clicks on "Do you want to see all our designs?"
    const seeAllLink = document.getElementById('see-all-designs');
    if (seeAllLink) {
      seeAllLink.addEventListener('click', () => {
        TRACKER.event('see_all_designs_clicked', {
          bundle_items: bundle.length,
          swipe_count: swipeCount,
          continuation: TRACKER.continuation,
        });
      });
    }
  }

  function startContinuation() {
    TRACKER.event('retake_started', { continuation: TRACKER.continuation + 1, swipe_count: swipeCount });
    // Keep all state (profileVector, shownIndices, likedIndices, surveyData)
    // Reset only what's needed for another round of swipes
    TRACKER.continuation++;
    isContinuation = true;
    quizFinished = false;
    pendingFinish = false;
    consecutiveStable = 0;
    previousTop40 = [];
    continuationSwipeStart = swipeCount;

    // Reset progress bar for continuation
    document.getElementById('progress-fill').style.width = '0%';
    document.getElementById('progress-text').textContent = '0%';

    // Clear cards
    const area = document.getElementById('card-stack');
    area.querySelectorAll('.swipe-card').forEach(c => c.remove());
    behindCardIndex = -1;

    // Load fresh cards and show swipe screen
    loadNextCards();
    showScreen('screen-swipe');

    // Tutorial hint
    setTimeout(() => {
      const topCard = document.querySelector('.swipe-card.top');
      if (topCard) {
        topCard.classList.add('tutorial-anim');
        topCard.addEventListener('animationend', () => {
          topCard.classList.remove('tutorial-anim');
        }, { once: true });
      }
    }, 400);
  }

  // ==================== PROFILE REVEAL ====================
  function setupProfileReveal() {
    const btn = document.getElementById('btn-build-bundle');
    if (btn) {
      btn.addEventListener('click', () => {
        TRACKER.event('build_bundle_clicked', {});
        showScreen('screen-scratch');
        // Wait for fade transition to complete so canvas gets correct dimensions
        setTimeout(() => initScratchCard(), 350);
      });
    }
  }

  // ==================== SCRATCH CARD ====================
  function drawGoldSurface(ctx, w, h) {
    const grad = ctx.createLinearGradient(0, 0, w, h);
    grad.addColorStop(0, '#b8860b');
    grad.addColorStop(0.15, '#d4a843');
    grad.addColorStop(0.3, '#f5e08a');
    grad.addColorStop(0.45, '#d4a843');
    grad.addColorStop(0.55, '#e8c84a');
    grad.addColorStop(0.7, '#f0d27a');
    grad.addColorStop(0.85, '#c9982a');
    grad.addColorStop(1, '#a07818');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, w, h);
    for (let y = 0; y < h; y += 2) {
      ctx.fillStyle = `rgba(255,255,255,${Math.random() * 0.04})`;
      ctx.fillRect(0, y, w, 1);
    }
    const bandGrad = ctx.createLinearGradient(w * 0.2, 0, w * 0.6, h);
    bandGrad.addColorStop(0, 'rgba(255,255,255,0)');
    bandGrad.addColorStop(0.4, 'rgba(255,255,255,0.12)');
    bandGrad.addColorStop(0.6, 'rgba(255,255,255,0.12)');
    bandGrad.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = bandGrad;
    ctx.fillRect(0, 0, w, h);
    for (let i = 0; i < 60; i++) {
      const sx = Math.random() * w, sy = Math.random() * h;
      ctx.beginPath();
      ctx.arc(sx, sy, Math.random() * 2 + 0.5, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(255,255,240,${Math.random() * 0.35 + 0.05})`;
      ctx.fill();
    }
    ctx.fillStyle = 'rgba(120,90,20,0.7)';
    ctx.font = '600 16px Halant, serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('SCRATCH TO REVEAL', w / 2, h / 2);
  }

  function initScratchCard() {
    const canvas = document.getElementById('scratch-canvas');
    const continueBtn = document.getElementById('btn-scratch-continue');
    const wrapper = document.getElementById('scratch-card-wrapper');
    const line1 = document.getElementById('scratch-line1');
    const line2 = document.getElementById('scratch-line2');
    continueBtn.disabled = true;

    // Hide deal text until canvas covers it
    line1.style.visibility = 'hidden';
    line2.style.visibility = 'hidden';

    // Set reveal text
    line1.textContent = 'BUY 2';
    line1.style.display = '';
    line2.textContent = 'GET 2 FREE';
    line2.style.display = '';
    wrapper.style.opacity = '1';

    // Clone canvas to remove old listeners
    const newCanvas = canvas.cloneNode(true);
    canvas.parentNode.replaceChild(newCanvas, canvas);
    const ctx = newCanvas.getContext('2d');
    newCanvas.width = wrapper.offsetWidth;
    newCanvas.height = wrapper.offsetHeight;
    drawGoldSurface(ctx, newCanvas.width, newCanvas.height);

    // Show deal text now that canvas covers it
    line1.style.visibility = '';
    line2.style.visibility = '';

    let isScratching = false;
    let revealed = false;
    let lastX = null, lastY = null;

    function scratch(x, y) {
      ctx.globalCompositeOperation = 'destination-out';
      ctx.lineWidth = 44;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      if (lastX !== null) {
        ctx.beginPath();
        ctx.moveTo(lastX, lastY);
        ctx.lineTo(x, y);
        ctx.stroke();
      }
      ctx.beginPath();
      ctx.arc(x, y, 22, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalCompositeOperation = 'source-over';
      lastX = x; lastY = y;
      checkReveal();
    }

    function getPos(e) {
      const rect = newCanvas.getBoundingClientRect();
      const touch = e.touches ? e.touches[0] : e;
      return {
        x: (touch.clientX - rect.left) * (newCanvas.width / rect.width),
        y: (touch.clientY - rect.top) * (newCanvas.height / rect.height)
      };
    }

    let scratchStartTracked = false;
    function onStart(e) { e.preventDefault(); isScratching = true; const p = getPos(e); scratch(p.x, p.y); if (!scratchStartTracked) { TRACKER.event('scratch_started', {}); scratchStartTracked = true; } }
    function onMove(e) { if (!isScratching) return; e.preventDefault(); const p = getPos(e); scratch(p.x, p.y); }
    function onEnd() { isScratching = false; lastX = null; lastY = null; }

    newCanvas.addEventListener('mousedown', onStart);
    newCanvas.addEventListener('mousemove', onMove);
    newCanvas.addEventListener('mouseup', onEnd);
    newCanvas.addEventListener('mouseleave', onEnd);
    newCanvas.addEventListener('touchstart', onStart, { passive: false });
    newCanvas.addEventListener('touchmove', onMove, { passive: false });
    newCanvas.addEventListener('touchend', onEnd);

    function checkReveal() {
      if (revealed) return;
      const cw = newCanvas.width, ch = newCanvas.height;
      const data = ctx.getImageData(0, 0, cw, ch);
      const tx1 = Math.floor(cw * 0.15), tx2 = Math.floor(cw * 0.85);
      const ty1 = Math.floor(ch * 0.10), ty2 = Math.floor(ch * 0.90);
      let textTrans = 0, textTotal = 0;
      for (let y = ty1; y < ty2; y++) {
        for (let x = tx1; x < tx2; x++) {
          textTotal++;
          if (data.data[(y * cw + x) * 4 + 3] === 0) textTrans++;
        }
      }
      if (textTrans / textTotal > 0.65) {
        revealed = true;
        TRACKER.event('scratch_revealed', {});
        ctx.clearRect(0, 0, cw, ch);
        continueBtn.disabled = false;
        launchConfetti();
      }
    }

    // Continue button → results (only bind once, only fire once)
    if (!continueBtn._bound) {
      continueBtn._bound = true;
      continueBtn.addEventListener('click', () => {
        if (continueBtn._fired) return;
        continueBtn._fired = true;
        TRACKER.event('scratch_continue_clicked', {});
        showScreen('screen-results');
      });
    }
  }

  function launchConfetti() {
    const colors = ['#d4a843', '#f0d27a', '#e8c84a', '#f5e08a', '#c9982a', '#b8922e', '#fff3c4'];
    const shapes = ['square', 'circle', 'strip'];
    for (let i = 0; i < 60; i++) {
      const el = document.createElement('div');
      el.className = 'confetti-piece falling';
      const shape = shapes[Math.floor(Math.random() * shapes.length)];
      const color = colors[Math.floor(Math.random() * colors.length)];
      const size = Math.random() * 8 + 5;
      el.style.left = Math.random() * 100 + 'vw';
      el.style.top = -(Math.random() * 30 + 10) + 'px';
      el.style.width = (shape === 'strip' ? size * 0.35 : size) + 'px';
      el.style.height = (shape === 'strip' ? size * 2.8 : size) + 'px';
      el.style.background = color;
      el.style.borderRadius = shape === 'circle' ? '50%' : (shape === 'strip' ? '2px' : '1px');
      el.style.setProperty('--duration', (Math.random() * 2 + 3) + 's');
      el.style.setProperty('--delay', (Math.random() * 1.2) + 's');
      el.style.setProperty('--drift', (Math.random() * 150 - 75) + 'px');
      el.style.setProperty('--spin', (Math.random() * 540 - 270) + 'deg');
      document.body.appendChild(el);
      setTimeout(() => el.remove(), 7000);
    }
  }

  function animateProfileStat() {
    const target = 87;
    const circumference = 326.73;
    const circle = document.querySelector('.stat-ring-progress');
    const numberEl = document.getElementById('stat-ring-number');
    const btn = document.getElementById('btn-build-bundle');
    if (!circle || !numberEl) return;

    // Button is always clickable immediately (no grey-out).
    if (btn) {
      btn.disabled = false;
      btn.style.opacity = '1';
      btn.style.cursor = 'pointer';
    }

    // Animate the ring (2s — halved from 4s)
    const duration = 2000;
    const offset = circumference - (target / 100) * circumference;
    circle.style.transition = 'stroke-dashoffset 2s ease-out';
    setTimeout(() => {
      circle.style.strokeDashoffset = offset;
    }, 100);

    // Animate the number counting up
    const startTime = performance.now();

    function step(now) {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = Math.round(eased * target);
      numberEl.textContent = current + '%';
      if (progress < 1) requestAnimationFrame(step);
    }

    setTimeout(() => requestAnimationFrame(step), 100);
  }

  // ==================== NAVIGATION ====================
  function showScreen(screenId) {
    const current = document.querySelector('.quiz-screen.active');
    const next = document.getElementById(screenId);
    TRACKER.event('screen_shown', { screen: screenId, from: current ? current.id : null });

    if (current && current.id !== screenId) {
      // Fade out current screen
      current.classList.add('fading');
      current.classList.remove('visible');
      setTimeout(() => {
        current.classList.remove('active', 'fading');
        // Show next screen with fade in
        next.classList.add('active');
        window.scrollTo({ top: 0 });
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            next.classList.add('visible');
          });
        });
        if (screenId === 'screen-results') startDealCountdown();
      }, 250);
    } else {
      // First screen or same screen — show immediately
      document.querySelectorAll('.quiz-screen').forEach(s => { s.classList.remove('active', 'visible', 'fading'); });
      next.classList.add('active');
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          next.classList.add('visible');
        });
      });
      if (screenId === 'screen-results') startDealCountdown();
    }
  }

  // ==================== DEAL COUNTDOWN ====================
  let countdownInterval = null;
  function startDealCountdown() {
    if (countdownInterval) return; // already running
    let remaining = 600; // 10 minutes in seconds
    const el = document.getElementById('deal-countdown');
    if (!el) return;
    function update() {
      const m = Math.floor(remaining / 60);
      const s = remaining % 60;
      el.textContent = m + ':' + (s < 10 ? '0' : '') + s;
      if (remaining <= 0) {
        clearInterval(countdownInterval);
        countdownInterval = null;
        el.textContent = '0:00';
        el.style.color = '#c0392b';
      }
      remaining--;
    }
    update();
    countdownInterval = setInterval(update, 1000);
  }

  // ==================== TRACKING ====================
  function trackEvent(type, data) {
    if (type === 'swipe') {
      const phase = data.rank <= CONFIG.calibrationSkus.length ? 'calibration' : 'adaptive';
      TRACKER.bufferSwipe(data.sku, data.direction, data.rank, data.dwellTime || 0, phase);
    }
  }

  // ==================== PRODUCT POPUP ====================
  function setupPopup() {
    const overlay = document.getElementById('product-popup');
    const closeBtn = document.getElementById('popup-close');

    // Close on X click
    closeBtn.addEventListener('click', closePopup);

    // Close on overlay click (outside content)
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) closePopup();
    });

    // Close on Escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') closePopup();
    });
  }

  function closePopup() {
    const overlay = document.getElementById('product-popup');
    overlay.classList.remove('open');
    document.body.style.overflow = '';
    TRACKER.event('popup_closed', {});
  }

  window._openPopup = async function(handle, sku) {
    const overlay = document.getElementById('product-popup');
    const body = document.getElementById('popup-body');
    const product = products[sku];
    const size = getSkuSize(sku);
    const price = parseFloat(product.p).toFixed(2);

    TRACKER.event('popup_opened', { sku, handle, title: product.t });

    // Show loading
    body.innerHTML = '<div class="popup-loading"><div class="loading-spinner"></div></div>';
    overlay.classList.add('open');
    document.body.style.overflow = 'hidden';

    // Use all images from catalog, fetch description from Shopify if available
    let images = product.imgs && product.imgs.length > 0 ? product.imgs : [product.img];
    let description = '';
    let variantId = null;

    try {
      const res = await fetch(`/products/${handle}.json`);
      if (res.ok) {
        const data = await res.json();
        const p = data.product;
        description = p.body_html || '';
        variantId = p.variants[0]?.id;
        // Use Shopify images if catalog didn't have multiple
        if (images.length <= 1 && p.images.length > 1) {
          images = p.images.map(img => img.src);
        }
      }
    } catch(e) {
      // Preview mode — images from catalog, no description
    }

    // Build gallery slides
    const slides = images.map(src => `
      <div class="swiper-slide">
        <img src="${src}" alt="${product.t}" loading="eager">
      </div>
    `).join('');

    body.innerHTML = `
      <div class="popup-gallery">
        <div class="swiper popup-swiper">
          <div class="swiper-wrapper">${slides}</div>
          ${images.length > 1 ? '<div class="swiper-pagination"></div>' : ''}
        </div>
      </div>
      <div class="popup-info">
        <h2 class="popup-product-title">${product.t}</h2>
        ${size ? `<p class="popup-product-size">${size}</p>` : ''}
        <p class="popup-product-price">$${price}</p>
        ${description ? `<div class="popup-product-desc">${description}</div>` : ''}
        <button class="popup-add-to-cart" data-handle="${handle}" data-sku="${sku}" ${variantId ? `data-variant="${variantId}"` : ''}>
          <svg viewBox="0 0 24 24" stroke-linecap="round" stroke-linejoin="round">
            <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/>
            <line x1="3" y1="6" x2="21" y2="6"/>
            <path d="M16 10a4 4 0 01-8 0"/>
          </svg>
          ADD TO CART
        </button>
      </div>
    `;

    // Init Swiper for gallery
    if (images.length > 1) {
      new Swiper('.popup-swiper', {
        speed: 300,
        loop: true,
        pagination: { el: '.swiper-pagination', clickable: true }
      });
    }

    // Add to bundle from popup
    body.querySelector('.popup-add-to-cart').addEventListener('click', function() {
      const btn = this;
      const popupSku = btn.dataset.sku;
      const popupHandle = btn.dataset.handle;
      const existing = bundle.find(b => b.sku === popupSku);
      if (existing) {
        existing.qty++;
      } else {
        const prod = products[popupSku];
        bundle.push({
          sku: popupSku, handle: popupHandle,
          title: prod.t, price: parseFloat(prod.p),
          img: prod.img, qty: 1, variantId: btn.dataset.variant ? parseInt(btn.dataset.variant) : null
        });
      }
      btn.innerHTML = `<svg viewBox="0 0 24 24" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg> ADDED`;
      btn.style.background = '#333';
      trackEvent('add_to_cart', { sku: popupSku, handle: popupHandle, source: 'popup' });
      TRACKER.sendBundleEvent('add', popupSku, parseFloat(products[popupSku].p), 1);
      TRACKER.event('popup_add_to_bundle', { sku: popupSku, handle: popupHandle, qty: existing ? existing.qty : 1 });
      // Update the grid card button to qty control
      renderCardButton(popupSku);
      updateBundleFooter();
    });
  };

  // ==================== ABANDONMENT TRACKING ====================
  function getCurrentScreen() {
    const visible = document.querySelector('.quiz-screen.active, .quiz-screen:not([style*="display: none"])');
    return visible ? visible.id : 'unknown';
  }

  // Track when user leaves/switches tab
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      TRACKER.event('tab_hidden', { screen: getCurrentScreen(), bundle_items: bundle.length, swipe_count: swipeCount });
    } else {
      TRACKER.event('tab_visible', { screen: getCurrentScreen() });
    }
  });

  // Track when user is about to leave the page entirely
  window.addEventListener('beforeunload', () => {
    TRACKER.event('page_exit', { screen: getCurrentScreen(), bundle_items: bundle.length, swipe_count: swipeCount });
    // Use sendBeacon for reliable delivery on page exit
    if (TRACKER._queue.length > 0 && TRACKER.sessionId) {
      const events = TRACKER._queue.splice(0);
      navigator.sendBeacon(
        `${TRACKER.endpoint}/api/events`,
        JSON.stringify({ events })
      );
    }
  });

  // ==================== SCROLL TRACKING ON RESULTS ====================
  let scrollTracked25 = false, scrollTracked50 = false, scrollTracked75 = false, scrollTracked100 = false;
  window.addEventListener('scroll', () => {
    const screen = getCurrentScreen();
    if (screen !== 'screen-results') return;
    const scrollPct = Math.round((window.scrollY / (document.body.scrollHeight - window.innerHeight)) * 100);
    if (scrollPct >= 25 && !scrollTracked25) { scrollTracked25 = true; TRACKER.event('results_scroll_25', {}); }
    if (scrollPct >= 50 && !scrollTracked50) { scrollTracked50 = true; TRACKER.event('results_scroll_50', {}); }
    if (scrollPct >= 75 && !scrollTracked75) { scrollTracked75 = true; TRACKER.event('results_scroll_75', {}); }
    if (scrollPct >= 100 && !scrollTracked100) { scrollTracked100 = true; TRACKER.event('results_scroll_100', {}); }
  });

  // ==================== START ====================
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => { setupRetake(); init(); setupPopup(); setupProfileReveal(); setupSleeveQuestion(); });
  } else {
    setupRetake(); init(); setupPopup(); setupProfileReveal(); setupSleeveQuestion();
  }
})();